// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import _ from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";
import { Loader } from "semantic-ui-react";

import * as protocolActions from "../../../../redux/actions/protocolActions";
import { ApplicationArea } from "../../../Lumber/ApplicationAreas";
import SearchPanel from "../../../Lumber/Search/SearchPanel.tsx";
import { generateStringFilter, getDefaultGrowingSeasons } from "../../../../utilities/search.ts";
import ProtocolSearchResults from "./ProtocolSearchResults.tsx";

const ProtocolLog = () => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	//-- DataSource
	const [originalProtocols, setOriginalProtocols] = useState([]);
	const [protocols, setProtocols] = useState([]);

	//-- Dropdown Options
	const [protocolOwnerOptions, setProtocolOwnerOptions] = useState([]);
	const [cropOptions, setCropOptions] = useState([]);
	const [growingSeasonOptions, setGrowingSeasonOptions] = useState([]);
	const [geographyOptions, setGeographyOptions] = useState([]);
	const [plotSizeOptions, setPlotSizeOptions] = useState([]);
	const [regulatoryOptions, setRegulatoryOptions] = useState([]);
	const [originalRegulatoryOptions, setOriginalRegulatoryOptions] = useState([]);
	const [variablesTestedOptions, setVariablesTestedOptions] = useState([]);
	const [originalVariablesTestedOptions, setOriginalVariablesTestedOptions] = useState([]);
	const [searchAllClients, setSearchAllClients] = useState(false);
	// const [hasSearchedAllClients, setHasSearchedAllClients] = useState(false);
	const [trialNameOptions, setTrialNameOptions] = useState([]);
	const [treatmentNameOptions, setTreatmentNameOptions] = useState([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [filters, setFilters] = useState([]);
	const [growingSeasons, setGrowingSeasons] = useState([]);

	//-- Data
	const [protocolInfo, setProtocolInfo] = useState(setInitialProtocolInfo());
	const [protocolOptions, setProtocolOptions] = useState([]);

	//-- UI Control
	const [loading, setLoading] = useState(false);

	const yieldOpts = useMemo(
		() => [
			{ text: "Yes", key: "yes", value: true },
			{ text: "No", key: "no", value: false },
		],
		[],
	);

	const regulatoryOpts = useMemo(() => {
		const opts = regulatoryOptions.map((ro) => ({
			text: ro.text,
			key: ro.key,
			value: ro.value,
		}));

		opts.push({ text: "No Regulatories", key: "regs-null", value: null });

		return opts;
	}, [regulatoryOptions]);

	const variablesTestedOpts = useMemo(() => {
		const opts = variablesTestedOptions.map((vto) => ({
			text: vto.text,
			key: vto.key,
			value: vto.value,
		}));

		opts.push({ name: "No Variables", key: "vars-null", value: null });

		return opts;
	}, [variablesTestedOptions]);

	useEffect(() => {
		if (userAuth.isReady && userAuth.currentClientId) {
			getDropdownOptions();
		}
	}, [userAuth.isReady, userAuth.currentClientId, searchAllClients]);

	useEffect(() => {
		if (_.some(protocolOptions)) {
			getProtocols();
		}
	}, [protocolOptions]);

	function setInitialProtocolInfo(growingSeason = null) {
		return {
			id: "all",
			protocolId: "all", //-- user defined id
			protocolName: "all",
			protocolOwnerId: "all",
			cropId: "all",
			growingSeasonId: growingSeason ?? "all",
			geographyId: "all",
			treatments: "all",
			replicates: "all",
			plotSizeId: "all",
			designId: "all",
			meanComparisonId: "all",
			alphaId: "all",
			yieldRequired: true,
			yieldNotRequired: true,
			regulatories: _.cloneDeep(originalRegulatoryOptions),
			variablesTested: _.cloneDeep(originalVariablesTestedOptions),
			rotation: "all",
			genetics: "all",
			herbicides: "all",
			fertilizer: "all",
			pesticides: "all",
			other: "all",
			objectives: "all",
			keyword: "",
			trialId: "all",
		};
	}

	function setInitialProtocolOwnerOptions(options) {
		let filteredOpt = _.uniqBy(options, "protocolOwnerId");
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.protocolOwnerId, value: o.protocolOwnerId, text: o.owner };
		});

		let orderedOptionList = _.orderBy(optionList, [(optionList) => optionList.text.toLowerCase().trim()]);
		setProtocolOwnerOptions(orderedOptionList);

		if (
			protocolInfo.protocolOwnerId &&
			!_.some(optionList, (o) => {
				return o.personId === protocolInfo.protocolOwnerId;
			})
		) {
			setProtocolInfo((protocolInfo) => ({ ...protocolInfo, protocolOwnerId: "all" }));
		}
	}

	function setInitialCropOptions(options) {
		let filteredOpt = _.uniqBy(options, "cropId");
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.cropId, value: o.cropId, text: o.cropName };
		});

		let orderedOptionList = _.orderBy(optionList, ["text"]);
		setCropOptions(orderedOptionList);
	}

	function setInitialGrowingSeasonOptions(options) {
		let filteredOpt = _.uniqBy(options, "growingSeasonId");
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.growingSeasonId, value: o.growingSeasonId, text: o.growingSeasonName };
		});

		let orderedOptionList = _.orderBy(optionList, ["text"], ["desc"]);
		setGrowingSeasonOptions(orderedOptionList);

		//-- Set growing season to default gs
		let defaultGrowingSeasons = getDefaultGrowingSeasons();
		let filteredSelections = _.map(
			_.filter(optionList, (gs) => defaultGrowingSeasons.includes(gs.text)),
			"value",
		);
		let defaultGrowingSeasonSelections = filteredSelections.length > 0 ? filteredSelections : [optionList[0]?.value];
		setGrowingSeasons(defaultGrowingSeasonSelections);
	}

	function setInitialGeographyOptions(options) {
		let filteredOpt = _.uniqBy(options, "geographyId");
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.geographyId, value: o.geographyId, text: o.geographyName };
		});

		let orderedOptionList = _.orderBy(optionList, ["text"]);
		setGeographyOptions(orderedOptionList);
	}

	function setInitialPlotSizeOptions(options) {
		let filteredOpt = _.uniqBy(options, "plotSizeId");
		let optionList = _.map(filteredOpt, (o) => {
			let plotSize = _.find(protocolOptions?.plotSizes, (po) => po.id === o.plotSizeId);
			if (plotSize) {
				return { key: o.plotSizeId, value: o.plotSizeId, text: `${plotSize.name} (${plotSize.description})` };
			}
		});

		let orderedOptionList = _.orderBy(optionList, ["text"]);
		setPlotSizeOptions(orderedOptionList);
	}

	function setInitialRegulatoryOptions(options) {
		let filteredOpt = _.uniqBy(
			_.map(
				_.filter(
					_.map(options, (o) => {
						return _.filter(o, (fo) => fo?.checked);
					}),
					(t) => {
						return t.length > 0;
					},
				),
				(v) => {
					return v[0];
				},
			),
			(u) => u.id,
		);
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.id, value: o.id, text: o.name, checked: false };
		});
		setProtocolInfo((protocolInfo) => ({ ...protocolInfo, regulatories: optionList }));
		let orderedOptionList = _.orderBy(optionList, ["text"]);
		setRegulatoryOptions(orderedOptionList);
		setOriginalRegulatoryOptions(optionList);
	}

	function setInitialVariablesTested(options) {
		let filteredOpt = _.uniqBy(
			_.map(
				_.filter(
					_.map(options, (o) => {
						return _.filter(o, (fo) => fo?.checked);
					}),
					(t) => {
						return t.length > 0;
					},
				),
				(v) => {
					return v[0];
				},
			),
			(u) => u.id,
		);
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.id, value: o.id, text: o.name, checked: false };
		});

		setProtocolInfo((protocolInfo) => ({ ...protocolInfo, variablesTested: optionList }));
		let orderedOptionList = _.orderBy(optionList, ["text"]);
		setVariablesTestedOptions(orderedOptionList);
		setOriginalVariablesTestedOptions(optionList);
	}

	function setInitialTrialNameOptions(options) {
		let filteredOpt = _.uniqBy(
			_.flatten(
				_.filter(_.map(options), (pt) => {
					return pt.length > 0;
				}),
			),
			(u) => u.trialId,
		);
		let optionList = _.map(filteredOpt, (o) => {
			return { key: o.trialId, value: o.trialName, text: o.trialName };
		});

		let orderedOptionList = _.orderBy(optionList, [(optionList) => optionList.text.toLowerCase().trim()]);
		orderedOptionList.push({ key: "trial-null", value: null, text: "No Trials" });
		setTrialNameOptions(orderedOptionList);
	}

	function setInitialTreatmentNameOptions(options) {
		let filteredOpt = _.uniqBy(
			_.flatten(
				_.filter(_.map(options), (pt) => {
					return pt.length > 0;
				}),
			),
			(u) => u.treatmentName,
		);
		let optionList = _.map(
			_.filter(filteredOpt, (o) => o.treatmentName),
			(o) => {
				return { key: o.treatmentId, value: o.treatmentName, text: o.treatmentName };
			},
		);

		let orderedOptionList = _.orderBy(optionList, [(optionList) => optionList.text.toLowerCase().trim()]);
		orderedOptionList.push({ key: "treatment-null", value: null, text: "No Treatments" });
		setTreatmentNameOptions(orderedOptionList);
	}

	async function getDropdownOptions() {
		setLoading(true);
		const accessToken = await getTokenSilently();
		dispatch(protocolActions.getProtocolInfoOptions(userAuth.currentClientId, searchAllClients, accessToken))
			.then((res) => {
				setProtocolOptions(res.data);
				setLoading(false);
			})
			.catch((err) => {
				toast.error(err);
				setLoading(false);
			});
	}

	async function getProtocols() {
		setLoading(true);
		const accessToken = await getTokenSilently();
		dispatch(protocolActions.getProtocols(userAuth.currentClientId, searchAllClients, accessToken)).then((res) => {
			res.data = _.filter(res.data, (d) => {
				if (userAuth.hasApplicationArea(ApplicationArea.ProtocolSearch, d.clientId)) {
					return d;
				}
			});
			//Set options
			const data = res.data.map((d) => {
				const temp = {
					...d,
					statuses: {
						hasExclusions: d.hasExclusions,
						hasFlaggings: d.hasFlaggings,
						hasPartialExclusions: d.hasPartialExclusions,
						hasPartialFlaggings: d.hasPartialFlaggings,
					},
				};

				temp.regulatories = temp.regulatories.filter((r) => r.checked);
				temp.protocolVariables = temp.protocolVariables.filter((pv) => pv.checked);

				return temp;
			});

			setOriginalProtocols(data);
			setProtocols(data);
			setDropdowns(data);

			res.data = _.filter(res.data, (d) => {
				return d.clientId === userAuth.currentClientId;
			});

			setLoading(false);
		});
	}

	const setDropdowns = (data) => {
		setInitialCropOptions(data);
		setInitialGrowingSeasonOptions(data);
		setInitialGeographyOptions(data);
		setInitialProtocolOwnerOptions(data);
		setInitialPlotSizeOptions(data);
		setInitialRegulatoryOptions(
			_.map(data, (rd) => {
				return rd.regulatories;
			}),
		);
		setInitialVariablesTested(
			_.map(data, (rd) => {
				return rd.protocolVariables;
			}),
		);
		setInitialTrialNameOptions(
			_.map(data, (rd) => {
				return rd.protocolTrials;
			}),
		);
		setInitialTreatmentNameOptions(
			_.map(data, (rd) => {
				return rd.protocolTreatments;
			}),
		);
	};

	const handleReset = () => {
		let resetProtocolInfo = setInitialProtocolInfo("all");
		setProtocolInfo(resetProtocolInfo);
		setSearchTerm("");
		setDropdowns(originalProtocols);
		setSearchAllClients(false);
	};

	const handleFilters = {
		owners: generateStringFilter("protocolOwnerId", setFilters, "owner"),
		crops: generateStringFilter("cropId", setFilters, "cropName"),
		growingSeasons: generateStringFilter("growingSeasonId", setFilters, "growingSeasonName"),
		geographies: generateStringFilter("geographyId", setFilters, "geographyName"),
		plotSizes: generateStringFilter("plotSizeId", setFilters, "plotSizeName"),
		trials: generateStringFilter("protocolTrials.trialName", setFilters, "protocolTrials"),
		treatments: generateStringFilter("protocolTreatments.treatmentName", setFilters),
		yield: generateStringFilter("yield", setFilters),
		regulatories: generateStringFilter("regulatories.id", setFilters, "regulatories"),
		variables: generateStringFilter("protocolVariables.id", setFilters, "protocolVariables"),
	};

	return loading ? (
		<Loader active />
	) : (
		<div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0" }}>
			<SearchPanel onReset={handleReset}>
				<SearchPanel.Bar
					searchTerm={searchTerm}
					setSearchTerm={setSearchTerm}
					placeholder="Search Protocols"
					disabled={loading}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Growing Seasons"
					options={growingSeasonOptions}
					defaultOptionIds={growingSeasons}
					setFilters={handleFilters.growingSeasons}
				/>
				<SearchPanel.MultiDropdown
					placeholder="Crops"
					options={cropOptions}
					setFilters={handleFilters.crops}
					size="small"
				/>
				<SearchPanel.MultiDropdown
					placeholder="Trials"
					options={trialNameOptions}
					setFilters={handleFilters.trials}
					size="small"
				/>
				<SearchPanel.MultiDropdown
					placeholder="Treatments"
					options={treatmentNameOptions}
					setFilters={handleFilters.treatments}
					size="small"
				/>
				<SearchPanel.MultiDropdown
					placeholder="Owner"
					options={protocolOwnerOptions}
					setFilters={handleFilters.owners}
					size="small"
				/>
				<SearchPanel.AdvancedPopup
					filters={filters}
					advancedHeaders={["yield", "regulatories", "protocolVariables", "geographyName", "plotSizeName"]}
				>
					<SearchPanel.MultiDropdown
						placeholder="Yield Required"
						options={yieldOpts}
						setFilters={handleFilters.yield}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Variables Tested"
						options={variablesTestedOpts}
						setFilters={handleFilters.variables}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Regulatories"
						options={regulatoryOpts}
						setFilters={handleFilters.regulatories}
					/>

					<SearchPanel.MultiDropdown
						placeholder="Geographies"
						options={geographyOptions}
						setFilters={handleFilters.geographies}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Plot Sizes"
						options={plotSizeOptions}
						setFilters={handleFilters.plotSizes}
					/>
					<SearchPanel.Checkbox
						label="Search All Clients"
						checked={searchAllClients}
						setChecked={setSearchAllClients}
					/>
				</SearchPanel.AdvancedPopup>
			</SearchPanel>
			<ProtocolSearchResults
				data={protocols}
				filters={filters}
				searchTerm={{
					term: searchTerm,
					cols: [
						"protocolName",
						"protocolTrials.trialName",
						"protocolTreatments.treatmentName",
						"clientName",
						"geographyName",
						"cropName",
						"growingSeasonName",
						"owner",
						"protocolVariables.name",
						"statuses",
					],
				}}
				loading={loading}
			/>
		</div>
	);
};

export default ProtocolLog;
